import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import EmptyState from "@/components/jea/EmptyState";
import JeaCard from "@/components/jea/JeaCard";
import Skeleton from "@/components/jea/Skeleton";
import { apiClient, formatApiError } from "@/lib/apiClient";
import { centsToUSD, shortDate } from "@/lib/dates";

const QueryHistoryTab = ({ debtorId }) => {
  const nav = useNavigate();
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await apiClient.get(`/debtors/${debtorId}/queries`);
        if (alive) setRuns(data.query_runs || []);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [debtorId]);

  if (loading) {
    return (
      <JeaCard className="p-0 overflow-hidden" data-testid="query-history-loading">
        <div className="p-6 space-y-3">
          {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-10" />)}
        </div>
      </JeaCard>
    );
  }

  if (runs.length === 0) {
    return (
      <EmptyState
        icon={Search}
        title="No queries run yet"
        description="Run your first broker query to populate this history."
        testid="query-history-empty"
      />
    );
  }

  return (
    <JeaCard className="p-0 overflow-hidden" data-testid="query-history-table">
      <table className="w-full">
        <thead>
          <tr className="text-left border-b border-border-default">
            {["Date", "Brokers", "Bundle", "Cost", "Report", ""].map((h) => (
              <th key={h} className="jea-caption text-text-secondary p-4">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {runs.map((r) => (
            <motion.tr
              key={r.id}
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.2 }}
              className="border-b border-border-default hover:bg-bg-tertiary transition-colors cursor-pointer"
              onClick={() => nav(`/debtors/${debtorId}/queries/${r.id}`)}
              data-testid={`query-history-row-${r.id}`}
            >
              <td className="p-4 jea-date text-[13px]">{shortDate(r.completed_at || r.started_at)}</td>
              <td className="p-4 text-white">{r.broker_slugs?.length ?? 0} broker(s)</td>
              <td className="p-4 text-text-secondary text-[13px] capitalize">
                {r.bundle ? r.bundle.replace(/_/g, " ") : "Custom"}
              </td>
              <td className="p-4 jea-mono text-accent-pink font-bold">{centsToUSD(r.total_cost_cents)}</td>
              <td className="p-4">
                {r.report_id ? (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); nav(`/debtors/${debtorId}/reports/${r.report_id}`); }}
                    className="text-accent-pink hover:text-accent-pink-light text-[13px] font-semibold"
                    data-testid={`query-history-view-report-${r.id}`}
                  >
                    View Report
                  </button>
                ) : (
                  <span className="text-text-muted text-[13px]">—</span>
                )}
              </td>
              <td className="p-4 text-right">
                <ChevronRight size={14} className="text-text-muted inline" />
              </td>
            </motion.tr>
          ))}
        </tbody>
      </table>
    </JeaCard>
  );
};

export default QueryHistoryTab;
